import type {UnlistenFn} from "@tauri-apps/api/event";
import {emit, listen} from "@tauri-apps/api/event";
import {invoke, isTauri} from "@tauri-apps/api/core";
import {getCurrentWindow} from "@tauri-apps/api/window";
import type {EventMessage, EventPayloads} from "../IntegrationEventTypes.ts";
import {EventType} from "../IntegrationEventTypes.ts";
import {IntegrationEventRouter} from "../IntegrationEventRouter.ts";
import {registerDeviceEventHandlers} from "../DeviceEventHandlers.ts";
import {
    getStoredEnvironmentUrl,
    resolveEnvironmentUrl,
    syncEnvironmentUrlToNativeUpdater,
} from "../../utils/EnvironmentUrl.ts";
import {BUNDLE_VERSION} from "../../utils/BundleVersion.ts";

export class TauriManager {
    private readonly router = new IntegrationEventRouter();
    private initialized = false;
    private active = false;
    private unlistenFns: UnlistenFn[] = [];
    private version: string | null = null;
    private fullscreenReasserted = false;

    public async init(): Promise<void> {
        if (this.initialized) return;
        this.initialized = true;
        this.active = isTauri();

        if (!this.active) {
            console.info("[TauriManager] Tauri not detected; skipping init.");
            return;
        }

        void syncEnvironmentUrlToNativeUpdater(getStoredEnvironmentUrl());
        void this.reassertFullscreenAfterStartup();

        const eventTypes = Object.values(EventType) as EventType[];
        for (const eventType of eventTypes) {
            const unlisten = await listen<unknown>(eventType, (event) => {
                this.router.dispatch(eventType, event.payload);
            });
            this.unlistenFns.push(unlisten);
        }

        this.registerUpdaterManifestUrlResponder();

        const stopAck = this.on(EventType.ReadyAck, (message) => {
            console.info("[TauriManager] Init ack received", message.payload);
            this.version = message.payload.backendVersion ?? null;
            stopAck();
        });

        await this.emit(EventType.Ready, {version: BUNDLE_VERSION ?? undefined});
        await this.loadEvents();
    }

    private async reassertFullscreenAfterStartup(): Promise<void> {
        // see docs/EXTRACTED_COMMENTS.md#c081
        if (!this.active || this.fullscreenReasserted) return;
        this.fullscreenReasserted = true;

        const win = getCurrentWindow();
        const delaysMs = [0, 150, 350, 750, 1500, 3000];

        for (const delayMs of delaysMs) {
            if (delayMs > 0) {
                await new Promise<void>((resolve) => setTimeout(resolve, delayMs));
            }
            try {
                await win.setFullscreen(true);
            } catch (error) {
                // Permissions or platform limitations shouldn't break startup.
                console.warn("[TauriManager] Failed to reassert fullscreen", error);
            }
        }
    }

    public getVersion(): string | null {
        return this.version;
    }

    public async loadEvents(): Promise<void> {
        registerDeviceEventHandlers((type, handler) => this.on(type, handler), "TauriManager");
    }

    private registerUpdaterManifestUrlResponder(): void {
        this.on(EventType.UpdaterManifestUrlRequest, (message) => {
            void this.respondToManifestUrlRequest(message.payload.requestId);
        });
    }

    private async respondToManifestUrlRequest(requestId: string): Promise<void> {
        const base = resolveEnvironmentUrl();
        const manifestUrl = new URL("/update/tauri/latest.json", base).toString();

        await this.emit(EventType.UpdaterManifestUrlResponse, {
            requestId,
            manifestUrl,
        });
    }

    public isAvailable(): boolean {
        return this.active;
    }

    public on<T extends EventType>(type: T, handler: (message: EventMessage<T>) => void): () => void {
        return this.router.on(type, handler);
    }

    public async emit<T extends EventType>(type: T, payload: EventPayloads[T]): Promise<void> {
        if (!this.active) return;
        await emit(type, payload);
    }

    public async request<T extends EventType>(
        type: T,
        payload: EventPayloads[T]
    ): Promise<EventMessage<EventType> | null> {
        if (!this.active) return null;
        const message: EventMessage<T> = {type, payload};
        return invoke<EventMessage<EventType>>("device_request", {message});
    }

    public async dispose(): Promise<void> {
        const pending = this.unlistenFns.map(async (unlisten) => {
            try {
                await unlisten();
            } catch (error) {
                console.warn("[TauriManager] Failed to unlisten", error);
            }
        });
        await Promise.all(pending);
        this.unlistenFns = [];
        this.router.clear();
        this.active = false;
        this.initialized = false;
    }

}

export const tauriManager = new TauriManager();
